import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { QuoteGetResponse } from '@lio/api-types';
import { RoleManagementDialog } from '@pages/QuoteDetails/dialogs';
import { ChangeLogModal } from '@components/modals/ChangeLogModal/ChangeLogModal';
import { Dialog } from '@components/dialog';
import { ToastType } from '@components/toast';
import { useUpdateQuote } from '@services/quotes';
import { QuoteType } from '@models/quotes';
import { getFormattedPolicyNumber } from '@utils/policy';
import { useGetPolicy } from '@services/policy';
import { useGetOoseResolution } from '@services/policy/useGetOoseResolution';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { FormControlLabel } from '@components/formControlLabel';
import { Checkbox } from '@components/checkbox/Checkbox';
import { BindQuoteModalTypes, BindQuoteModalType } from '@pages/BindQuote/BindQuoteDialogs/BindQuoteDialogs.constants';
import { SubmissionSummary } from '@pages/BindQuote/SubmissionSummary/SubmissionSummary';
import { ConfirmationDialog } from '@components/dialog/ConfirmationDialog';
import { useAlertStore } from '@store/AlertStore';

export type BindQuoteDialogsProps = {
  quote: QuoteGetResponse;
  onNavigate: () => void;
  onBindQuote: () => void;
  onContinuePayment?: () => Promise<void>;
  modalType: BindQuoteModalType | undefined;
  setModalType: (value: BindQuoteModalType | undefined) => void;
};

export const BindQuoteDialogs = ({
  quote,
  onNavigate,
  onBindQuote,
  onContinuePayment,
  modalType,
  setModalType,
}: BindQuoteDialogsProps) => {
  const isPaymentDisabled = useAlertStore((state) => state.isPaymentDisabled, shallow);
  const quoteShouldShowPaymentDisabled = isPaymentDisabled && quote?.offering?.requirePreBindPayment ? true : false;
  const { enqueueSnackbar } = useSnackbar();
  const [userAuth] = useStore((state) => [state.userAuth], shallow);
  const { isInternalUser } = userAuth;
  const { updateQuote, isPending: isUpdateQuoteLoading } = useUpdateQuote();
  const [acceptance, setAcceptance] = useState(false);
  const navigate = useNavigate();
  const isEndorsement = quote.quoteType === QuoteType.Endorsement;
  const isRenewal = quote.quoteType === QuoteType.Renewal;
  const { data: policy } = useGetPolicy({ policyId: isEndorsement || isRenewal ? quote?.policyId : undefined });
  const { ooseResolution } = useGetOoseResolution({ policyId: isEndorsement ? quote?.policyId : undefined });
  const isOoseEndorsement =
    isEndorsement &&
    ooseResolution?.status === 'InProgress' &&
    ooseResolution.oosePolicyTransactionId === quote.policyTransaction?.id;
  // A staged quote is an endorsement created by the OoSE resolution wizard for re-rating.
  // Reliable detection: staged quotes have no policyTransaction (never bound to one) but do
  // have endorsementSourceQuoteId set. This avoids depending on ooseResolution loading, which
  // would cause hideDescription=false temporarily and make the modal spinner hang indefinitely
  // when useSearchPolicyTransactions finds no matching transaction for the staged quoteId.
  const isStagedQuote =
    isEndorsement && !isOoseEndorsement && !quote.policyTransaction && !!quote.endorsementSourceQuoteId;

  const formattedPolicyNumber = policy
    ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)
    : '...';

  const handleShowUpdateQuoteSnackbar = ({ isUpdateQuoteError }: { isUpdateQuoteError: boolean }) => {
    let snackbarMessage = 'Updating quote roles has succeeded';
    let snackbarVariant = ToastType.Success;
    if (isUpdateQuoteError) {
      snackbarMessage = 'Updating quote roles has failed';
      snackbarVariant = ToastType.Error;
    }
    enqueueSnackbar(snackbarMessage, {
      preventDuplicate: true,
      variant: snackbarVariant,
    });
  };

  const handleCheckboxChange = () => {
    setAcceptance((prev) => !prev);
  };

  const handleCloseDialog = () => {
    setModalType(undefined);
  };

  switch (modalType) {
    case BindQuoteModalTypes.exitDialog:
      return (
        <ConfirmationDialog
          isOpen={modalType !== undefined}
          onCancel={handleCloseDialog}
          description='There are unsaved changes. Exit without saving?'
          buttonConfig={{
            confirmText: 'Yes, edit submission',
            secondaryText: 'No, return to review',
            onConfirm: onNavigate,
          }}
        />
      );
    case BindQuoteModalTypes.multiPolicyBind:
      return (
        <Dialog
          open={modalType !== undefined}
          title='Bind Multiple Policies'
          secondaryBtnAction={handleCloseDialog}
          primaryBtnAction={() => {
            handleCloseDialog();
            onBindQuote();
          }}
          handleCloseDialog={handleCloseDialog}
          secondaryBtnText='Go Back'
          primaryBtnText='Bind & Generate Policies'
          showBorders={false}
          isPrimaryBtnDisabled={isEndorsement === true && !acceptance}
          description={
            <Box sx={{ display: 'flex', justifyContent: 'left', padding: '16px 24px' }}>
              {isEndorsement ? (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant='body'>
                      You are about to bind an endorsement on {formattedPolicyNumber} that will create a new related
                      policy. If this policy was originally bound with a similar related policy, they need to be
                      endorsed separately. Please be sure this is your intention before binding.
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      label="I'm sure that I want to bind this endorsement"
                      typographyProps={{ variant: 'bodySmall', fontWeight: 'bold' }}
                      name='confirmation'
                      sxProps={{ formControlLabel: { marginLeft: '-8px' } }}
                      control={<Checkbox onChange={handleCheckboxChange} />}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography variant='body'>
                  You are about to bind your {quote.offering.label ?? quote.offering.insuranceProduct.name} quote, which
                  includes lines of insurance that will be bound as separate policies. Are you sure you want to proceed?
                </Typography>
              )}
            </Box>
          }
        />
      );
    case BindQuoteModalTypes.rolesManagement:
      return (
        <RoleManagementDialog
          showDialog={quote && modalType === BindQuoteModalTypes.rolesManagement}
          closeLinkDialog={handleCloseDialog}
          quote={quote}
          onUpdateQuote={updateQuote}
          isUpdateQuoteLoading={isUpdateQuoteLoading}
          onShowUpdateQuoteSnackbar={handleShowUpdateQuoteSnackbar}
        />
      );
    case BindQuoteModalTypes.changelog:
      return (
        <ChangeLogModal
          quote={quote}
          handleBindQuote={() => {
            // Staged quotes return directly to the resolution wizard — no bind attempt.
            if (isStagedQuote && ooseResolution?.policyId) {
              setModalType(undefined);
              navigate(`/policy/${ooseResolution.policyId}/oose-resolution`);
              return;
            }
            if (quote.multiPolicyQuoteDestinations.length > 0) {
              setModalType(BindQuoteModalTypes.multiPolicyBind);
              return;
            }

            onBindQuote();
          }}
          isOpen={
            (quote?.quoteType === QuoteType.Endorsement || quote?.quoteType === QuoteType.Renewal) &&
            modalType === BindQuoteModalTypes.changelog
          }
          setIsOpen={() => setModalType(undefined)}
          bindPage
          policyNumber={isEndorsement || isRenewal ? formattedPolicyNumber : (quote?.policyNumber?.policyNumber ?? '')}
          sourceQuoteId={isRenewal ? policy?.boundQuote?.id : undefined}
          hideDescription={isRenewal || isStagedQuote}
          submitButtonLabel={
            isRenewal ? 'Process Renewal' : isOoseEndorsement || isStagedQuote ? 'Submit for Resolution' : undefined
          }
          isExternalUser={!isInternalUser}
          showFormsSection={isRenewal || isStagedQuote}
        />
      );
    case BindQuoteModalTypes.submissionSummary:
      return (
        <SubmissionSummary
          open={modalType !== undefined}
          handleClose={handleCloseDialog}
          quoteId={quote.id}
          handleContinuePayment={onContinuePayment}
          payAndBind
          isPaymentDisabled={quoteShouldShowPaymentDisabled}
        />
      );
    default:
      return null;
  }
};
